<template>
    <InfoBaseForm
        v-model="formData"
        :is-loading="isLoading"
        :is-show-validation-error="isShowValidationError"
        :is-name-field-readonly="props.editableExtraService && isEcoCategory"
        data-cy="info-extra-services-form"
        @save="save()"
        @cancel="emit('cancel')"
    >
        <template v-slot:center-block>
            <v-row v-if="formData.hasOwnProperty('extraServiceCategory')" dense>
                <v-col cols="12" sm="9" md="4">
                    <v-select
                        v-model="formData.extraServiceCategory"
                        :items="useCategories"
                        :label="$t('category')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="category"
                        class="mb-3"
                        clearable
                        hide-details
                    />
                </v-col>
            </v-row>
            <v-row v-if="formData.hasOwnProperty('extraServiceCategoryName')" dense>
                <v-col cols="12" sm="9" md="4">
                    <v-select
                        v-model="formData.extraServiceCategoryName"
                        :items="enhancedServiceCategories"
                        :label="`${$t('service_category')} *`"
                        item-value="type"
                        item-title="name"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="extra-service-category"
                        class="mb-3"
                        clearable
                        hide-details
                        :rules="[requiredRule('service_category')]"
                        :disabled="props.editableExtraService && isEcoCategory"
                        @update:model-value="updateCheckIn"
                    />
                </v-col>
            </v-row>
            <v-row
                v-if="
                    serviceType === contractServiceType.ownHotel &&
                    (formData.extraServiceCategoryName === 'EARLY_CHECKIN' ||
                        formData.extraServiceCategoryName === 'LATE_CHECKOUT')
                "
                dense
                class="pb-4"
            >
                <v-col cols="12" sm="9" md="6">
                    <div v-if="formData.extraServiceCategoryName === 'EARLY_CHECKIN'" class="text-subtitle-1 pb-2">
                        {{ $t('possible_check_in') }}
                    </div>
                    <div v-if="formData.extraServiceCategoryName === 'LATE_CHECKOUT'" class="text-subtitle-1 pb-2">
                        {{ $t('possible_check_out') }}
                    </div>
                    <v-row dense class="align-center pb-4">
                        <v-select
                            v-model="formData.startTime"
                            :items="useStartTime"
                            :label="`${$t('from')} *`"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="start-time"
                            hide-details
                            :rules="[requiredRule('from')]"
                            max-width="120px"
                        />
                        <span class="mx-2"> - </span>
                        <v-select
                            v-model="formData.endTime"
                            :items="useEndTime"
                            :label="`${$t('to')} *`"
                            density="compact"
                            variant="outlined"
                            color="primary"
                            data-cy="end-time"
                            hide-details
                            :rules="[requiredRule('to')]"
                            max-width="120px"
                        />
                    </v-row>
                    <v-radio-group v-model="formData.immediateConfirmation" density="compact" hide-details>
                        <v-radio
                            :label="$t('immediate_confirmation_of_check')"
                            :value="true"
                            color="primary"
                            data-cy="immediate-confirmation"
                        />
                        <v-radio :label="$t('check_on_request')" :value="false" color="primary" data-cy="on-request" />
                    </v-radio-group>
                </v-col>
            </v-row>
            <v-row v-if="formData.hasOwnProperty('categoryId')" dense>
                <v-col cols="12" sm="9" md="4">
                    <v-select
                        v-model="formData.categoryId"
                        :items="props.extraServiceCategories"
                        :label="`${$t('category')} *`"
                        item-title="label"
                        item-value="id"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="category"
                        class="mb-3"
                        clearable
                        hide-details
                        :disabled="!props.extraServiceCategories?.length"
                        :rules="props.extraServiceCategories?.length ? [requiredRule('category')] : []"
                    />
                </v-col>
            </v-row>
            <v-row v-if="formData?.extraServiceCategory === CharterExtraServiceCategory.Baggage" dense>
                <v-col cols="12" sm="3" md="1">
                    <v-text-field
                        v-model="formData.amount"
                        type="number"
                        :label="$t('amount')"
                        hide-spin-buttons
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="amount"
                    />
                </v-col>
                <v-col cols="12" sm="3" md="2">
                    <v-select
                        v-model="formData.unitOfMeasure"
                        :items="useUnitOfMeasure"
                        :label="$t('product.info.unit_of_measure')"
                        density="compact"
                        variant="outlined"
                        color="primary"
                        data-cy="unit-of-measure"
                        class="mb-3"
                        hide-details
                    />
                </v-col>
                <v-col cols="12" sm="3" md="1">
                    <v-text-field
                        v-model="formData.limit"
                        type="number"
                        :label="$t('limit')"
                        hide-spin-buttons
                        variant="outlined"
                        color="primary"
                        density="compact"
                        data-cy="limit"
                    />
                </v-col>
            </v-row>
            <!-- Energy-related fields section removed as per requirements -->

            <!-- Original price and calculation section -->
            <v-row dense>
                <v-col v-if="formData.hasOwnProperty('unitPricingType')" cols="12" sm="6" md="4">
                    <div class="text-subtitle-1">{{ $t('price_per') }}:</div>
                    <v-radio-group
                        v-model="formData.unitPricingType"
                        density="compact"
                        hide-details
                        :disabled="isEcoCategory"
                        @update:model-value="resetPaxes()"
                    >
                        <v-radio
                            v-for="{value, title} in useUnitPricing"
                            :key="value"
                            :label="title"
                            :value="value"
                            color="primary"
                            :data-cy="value"
                        />
                    </v-radio-group>
                </v-col>
                <v-col v-if="formData.hasOwnProperty('calculationType')" cols="12" sm="6" md="4">
                    <div class="text-subtitle-1">{{ $t('calculation_type') }}:</div>
                    <v-radio-group
                        v-model="formData.calculationType"
                        density="compact"
                        hide-details
                        :disabled="isEcoCategory"
                    >
                        <v-radio
                            v-for="calculation in useCalculations"
                            :key="calculation.value"
                            :label="`${$t(`calculationType.${calculation.value}`)}`"
                            :value="calculation.value"
                            :disabled="calculation.disabled || isEcoCategory"
                            color="primary"
                            :data-cy="calculation.value"
                        />
                    </v-radio-group>
                </v-col>
            </v-row>

            <!-- Offer this extra service block - only for non-eco categories -->
            <v-row v-if="formData.hasOwnProperty('enabledAllForServices') && !isEcoCategory" dense>
                <v-col cols="12" :md="props.serviceType === contractServiceType.ownHotel ? 6 : 4">
                    <div class="text-subtitle-1">{{ $t('product.info.suggest_extra_service') }}:</div>
                    <v-radio-group
                        v-if="props.serviceType !== contractServiceType.ownHotel"
                        v-model="formData.enabledAllForServices"
                        density="compact"
                        hide-details
                    >
                        <v-radio
                            :label="$t('product.info.always')"
                            :value="true"
                            color="primary"
                            data-cy="enabled-all-for-services"
                        />
                        <v-radio
                            :label="useSuggestTitle"
                            :value="false"
                            color="primary"
                            data-cy="enabled-for-services"
                        />
                    </v-radio-group>
                    <div v-if="props.serviceType === contractServiceType.ownHotel" class="pb-8">
                        <v-tabs v-model="serviceTab" center-active color="primary" height="30" class="mb-6">
                            <v-tab v-for="tab in tabs" :key="tab" class="text-caption text-primary">
                                {{ $t(`product.${tab}`) }}
                            </v-tab>
                        </v-tabs>
                        <v-tabs-window v-model="serviceTab">
                            <v-tabs-window-item v-for="tab in tabs" :key="tab" :value="tab">
                                <template v-if="tab === 'room_type'">
                                    <v-data-table
                                        v-model="formData.enabledForServices"
                                        :headers="useServiceHeaders"
                                        :items="useActiveRoomTypes"
                                        disable-sort
                                        density="compact"
                                        color="primary"
                                        class="elevation-0 table-no-border"
                                        data-cy="services-table"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="useActiveRoomTypes.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    >
                                        <!-- UI-25: append a sustainability suffix so Basic /
                                             Sustainable / Zero variants of the same base room are
                                             distinguishable. Basic rooms keep no suffix. -->
                                        <template #item.name="{item}">
                                            {{ roomTypeDisplayName(item) }}
                                        </template>
                                    </v-data-table>
                                </template>
                                <template v-if="tab === 'meal_type'">
                                    <v-data-table
                                        v-model="formData.enabledForSecondaryServices"
                                        :headers="useServiceHeaders"
                                        :items="useActiveMealTypes"
                                        disable-sort
                                        density="compact"
                                        color="primary"
                                        class="elevation-0 table-no-border"
                                        data-cy="services-table"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                        :height="useActiveMealTypes.length > 8 ? 350 : 'auto'"
                                        fixed-header
                                    >
                                        <template v-slot:item.name="{item}">
                                            {{ item.name }}
                                        </template>
                                    </v-data-table>
                                </template>
                                <template v-if="tab === 'allocation_type'">
                                    <v-data-table
                                        v-model="formData.enabledForPlaceTypes"
                                        :headers="useServiceHeaders"
                                        :items="useAllocationTypes"
                                        disable-sort
                                        density="compact"
                                        color="primary"
                                        class="elevation-0 table-no-border"
                                        data-cy="services-table"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                    />
                                </template>
                                <template v-if="tab === 'membership_level'">
                                    <v-data-table
                                        v-model="formData.membershipLevels"
                                        :headers="useMemberShipLevelHeaders"
                                        :items="membershipLevels"
                                        disable-sort
                                        density="compact"
                                        color="primary"
                                        class="elevation-0 table-no-border"
                                        data-cy="services-table"
                                        :items-per-page="-1"
                                        hide-default-footer
                                        show-select
                                    >
                                        <template v-slot:item.code="{item}">
                                            {{ $t(`crm_membership_level.${(item as any).code}`) }}
                                        </template>
                                    </v-data-table>
                                </template>
                            </v-tabs-window-item>
                        </v-tabs-window>
                    </div>
                </v-col>
            </v-row>
            <v-row
                v-if="
                    formData.hasOwnProperty('enabledAllForServices') &&
                    !formData.enabledAllForServices &&
                    props.serviceType !== contractServiceType.ownHotel
                "
                class="mb-4"
            >
                <v-col cols="12" md="8" class="mb-4">
                    <v-data-table
                        v-if="
                            formData.hasOwnProperty('enabledForServiceCategories') &&
                            props.serviceType === contractServiceType.ownCharter
                        "
                        v-model="formData.enabledForServiceCategories"
                        :headers="useHeaders"
                        :items="useCharterServiceCategories"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 table-server-selected"
                        data-cy="services-table"
                        :items-per-page="-1"
                        hide-default-footer
                        show-select
                    >
                        <template v-slot:item.active="{item}">
                            {{ item.active ? $t('active') : $t('inactive') }}
                        </template>
                    </v-data-table>
                    <v-data-table
                        v-if="formData.hasOwnProperty('enabledForServices')"
                        v-model="formData.enabledForServices"
                        :headers="useHeaders"
                        :items="useProductServices"
                        disable-sort
                        density="compact"
                        color="primary"
                        class="elevation-1 table-server-selected"
                        data-cy="services-table"
                        :items-per-page="-1"
                        hide-default-footer
                        show-select
                    >
                        <template v-slot:item.active="{item}">
                            {{ item.active ? $t('active') : $t('inactive') }}
                        </template>
                        <template v-slot:item.categoryId="{item}">
                            {{ useCatalogue.find(({id}) => id === item.categoryId)?.name || item.categoryId }}
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
            <InfoExtraServicesCarHireForm v-if="formData.extraServiceApplicationType" v-model="formData" />
        </template>
    </InfoBaseForm>
</template>

<script setup lang="ts">
    import {requiredRule, moreThanZero, minIntervalRules, maxIntervalRules} from '~/composables/rules'
    import {CharterExtraServiceCategory, contractServiceType, UnitOfMeasure} from '~/utils/types/types'
    import {
        initAccommodationExtraServices as coreInitAccommodationExtraServices,
        initCarHireExtraServices,
        initCharterExtraServices,
        initCruiseExtraServices,
        initEventExtraServices,
        initExcursionExtraServices,
        initTransferExtraServices,
        initVisaExtraServices,
    } from '~/utils/types/account/products/helpers'
    import InfoBaseForm from './InfoBaseForm.vue'
    import InfoExtraServicesCarHireForm from '~/components/products/information/InfoExtraServicesCarHireForm.vue'
    import {useCatalogue, useCharterServiceCategories, useProductServices} from '~/composables/form-helpers'

    // ZSH override for initAccommodationExtraServices to default to BY_NIGHT
    const initAccommodationExtraServices = () => {
        const coreData = coreInitAccommodationExtraServices()
        return {
            ...coreData,
            calculationType: 'BY_NIGHT', // ZSH override: default to BY_NIGHT instead of BY_SERVICE
        }
    }

    const localized = useLocalized()

    const props = defineProps<{
        editableExtraService?: any | null
        contractId: number
        serviceType: string
        guestsCategories?: any[]
        extraServiceCategories?: any[]
        appMethod: string
    }>()

    const emit = defineEmits<{
        (e: 'save' | 'cancel'): void
    }>()

    const app = useNuxtApp()
    const {t} = useI18n()

    const formData = ref<any>(initBaseFormData())
    const isLoading = ref<boolean>(false)
    const paxes = ref([])
    const serviceTab = ref<string>()
    const tabs = ['room_type', 'meal_type', 'allocation_type', 'membership_level']
    const isShowValidationError = ref<boolean>(false)

    // Energy-related validation rules removed as per requirements

    // Check if selected category is one of the eco categories
    const isEcoCategory = computed(() => {
        return (
            formData.value.extraServiceCategoryName === 'ECO_AMENITIES' ||
            formData.value.extraServiceCategoryName === 'ENERGY_SAVING_SOLUTIONS'
        )
    })

    // Energy-related computed properties and watchers removed as per requirements

    // Add eco categories to the service categories dropdown only when editing
    const enhancedServiceCategories = computed(() => {
        const ecoCategories = [
            {type: 'ECO_AMENITIES', name: 'Eco Amenities'},
            {type: 'ENERGY_SAVING_SOLUTIONS', name: 'Energy-Saving Solutions'},
        ]

        // Only include eco categories when editing an existing service
        if (props.editableExtraService) {
            return [...ecoCategories, ...(props.extraServiceCategories || [])]
        } else {
            // When creating a new service, don't show eco categories
            return props.extraServiceCategories || []
        }
    })

    // Watch for changes in the extraServiceCategoryName and set appropriate defaults
    watch(
        () => formData.value.extraServiceCategoryName,
        newCategory => {
            if (newCategory === 'ECO_AMENITIES' || newCategory === 'ENERGY_SAVING_SOLUTIONS') {
                // For eco categories, set to Service / By night
                formData.value.unitPricingType = 'PER_UNIT'
                formData.value.calculationType = 'BY_NIGHT'
            } else if (
                newCategory &&
                formData.value.hasOwnProperty('unitPricingType') &&
                formData.value.hasOwnProperty('calculationType')
            ) {
                // For ZSH tenant hotel services, only set BY_NIGHT for NEW services (not when editing existing ones)
                formData.value.unitPricingType = 'PER_UNIT'
                if (!props.editableExtraService) {
                    formData.value.calculationType = 'BY_NIGHT'
                }
            }
        }
    )

    // Watch for service name changes removed as per requirements

    // Watch for calculation type changes to handle BY_SERVICE -> BY_NIGHT transition
    watch(
        () => formData.value.calculationType,
        (newValue, oldValue) => {
            // For ZSH hotel services, if user changes from BY_SERVICE to BY_NIGHT,
            // we should remove BY_SERVICE from available options to prevent going back
            if (
                props.serviceType === contractServiceType.ownHotel &&
                props.editableExtraService &&
                oldValue === 'BY_SERVICE' &&
                newValue === 'BY_NIGHT'
            ) {
                // The computed property will automatically update to exclude BY_SERVICE
                // since formData.value.calculationType is no longer 'BY_SERVICE'
            }
        }
    )

    onMounted(async () => {
        if (props.guestsCategories) {
            paxes.value = clone(props.guestsCategories)
            if (!props.editableExtraService) {
                resetPaxes()
            }
        }
        if (useIsLoadServiceCategories.value && !useCatalogue.value?.length) await supplierStore.loadCatalogue()
        if (props.serviceType === contractServiceType.ownHotel) {
            if (!supplierStore.membershipLevels.length) await supplierStore.loadMembershipLevels()
            if (!supplierStore.mealTypes.length) await supplierStore.loadHotelSecondaryServices()
            await loadServiceLevels()

            // After data is loaded, populate the arrays for new services
            if (!props.editableExtraService) {
                // Enable all room types
                formData.value.enabledForServices = useActiveRoomTypes.value?.map(({id}) => id) || []

                // Enable all meal types
                formData.value.enabledForSecondaryServices = useActiveMealTypes.value?.map(({id}) => id) || []

                // Enable all membership levels
                formData.value.membershipLevels = membershipLevels.value?.map(({id}) => id) || []

                // Enable all allocation types
                formData.value.enabledForPlaceTypes = useAllocationTypes.value.map(({id}) => id)
            }
        }
        if (props.editableExtraService) {
            formData.value = usePreparedExtraService.value

            // If editing an eco category, ensure the pricing fields are set correctly
            if (isEcoCategory.value) {
                formData.value.unitPricingType = 'PER_UNIT'
                formData.value.calculationType = 'BY_NIGHT'
            }

            // Always set forAllPlaceTypes to true when editing
            formData.value.forAllPlaceTypes = true

            // Populate enabledForPlaceTypes with all allocation types
            if (props.serviceType === contractServiceType.ownHotel) {
                formData.value.enabledForPlaceTypes = useAllocationTypes.value.map(({id}) => id)
            }
        } else {
            // Energy-related fields initialization removed as per requirements

            // Ensure required fields are initialized
            formData.value.enabledForServiceCategories = formData.value.enabledForServiceCategories || []
            formData.value.immediateConfirmation = formData.value.immediateConfirmation || false
            formData.value.forAllPlaceTypes = true

            // Populate enabledForPlaceTypes with all allocation types
            if (props.serviceType === contractServiceType.ownHotel) {
                formData.value.enabledForPlaceTypes = useAllocationTypes.value.map(({id}) => id)
            }

            // Set default values for pricing fields based on category
            if (isEcoCategory.value) {
                formData.value.unitPricingType = 'PER_UNIT'
                formData.value.calculationType = 'BY_NIGHT'
            } else if (
                formData.value.hasOwnProperty('unitPricingType') &&
                formData.value.hasOwnProperty('calculationType')
            ) {
                formData.value.unitPricingType = 'PER_UNIT'
                // For ZSH tenant hotel services, only set BY_NIGHT for NEW services (not when editing existing ones)
                if (!props.editableExtraService) {
                    formData.value.calculationType = 'BY_NIGHT'
                }
            }

            // For new hotel services, ensure all tabs are enabled (forAllSecondaryServices === true)
            if (props.serviceType === contractServiceType.ownHotel) {
                // Set the flags to true first
                formData.value.enabledAllForServices = true
                formData.value.forAllSecondaryServices = true
                formData.value.forAllMembershipLevels = true
                formData.value.forAllPlaceTypes = true
            }
        }
        if (props.serviceType === contractServiceType.ownHotel) {
            // Populate arrays based on "forAll" flags to sync visual state with logical state
            if (formData.value.enabledAllForServices && useActiveRoomTypes.value?.length) {
                formData.value.enabledForServices = useActiveRoomTypes.value.map(({id}) => id)
            } else {
                formData.value.enabledForServices = formData.value.enabledForServices.filter(id =>
                    useActiveRoomTypes.value.some(obj => obj.id === id)
                )
            }

            if (formData.value.forAllSecondaryServices && useActiveMealTypes.value?.length) {
                formData.value.enabledForSecondaryServices = useActiveMealTypes.value.map(({id}) => id)
            } else {
                formData.value.enabledForSecondaryServices = formData.value.enabledForSecondaryServices.filter(id =>
                    useActiveMealTypes.value.some(obj => obj.id === id)
                )
            }

            if (formData.value.forAllMembershipLevels && membershipLevels.value?.length) {
                formData.value.membershipLevels = membershipLevels.value.map(({id}) => id)
            }

            if (formData.value.forAllPlaceTypes) {
                formData.value.enabledForPlaceTypes = useAllocationTypes.value.map(({id}) => id)
            }
        }
    })

    if (props.serviceType === contractServiceType.ownCarRent) {
        watch(
            () => formData.value,
            () => {
                isShowValidationError.value =
                    formData.value.extraServiceApplicationType === 'OFFICES_COMBINATION' &&
                    formData.value.officeCombinations?.length === 0
            },
            {deep: true}
        )
    }

    const useActiveRoomTypes = computed(() => {
        return useProductServices.value?.filter(({active}) => active)
    })

    // UI-25: ownProductServices (the room list) do not carry sustainabilityLevel, so we
    // resolve each service's level via ownHotelServices (serviceId -> hotelRoomTypeId) +
    // the hotel room types (roomTypeId -> sustainabilityLevel) — the same mapping the
    // Availability table and the Room types tab rely on. Basic rooms get no suffix.
    const serviceLevelMap = ref<Record<string | number, string>>({})

    async function loadServiceLevels() {
        if (props.serviceType !== contractServiceType.ownHotel) return
        const hotelId = supplierStore.hotelInfo?.hotelId
        if (!hotelId) return
        try {
            const {services: hotelServices = []} = await app.$api.ownHotelServices.get({contractId: props.contractId})
            const {hotelRoomTypes = []} = await app.$api.hotelDescriptionRoomType.get(hotelId, {locale: 'all'})
            const roomTypeLevel: Record<string | number, string> = {}
            hotelRoomTypes.forEach(rt => {
                roomTypeLevel[rt.id] = rt.sustainabilityLevel || 'BASIC'
            })
            const map: Record<string | number, string> = {}
            hotelServices.forEach(service => {
                if (service.hotelRoomTypeId != null) {
                    map[service.id] = roomTypeLevel[service.hotelRoomTypeId] || 'BASIC'
                }
            })
            serviceLevelMap.value = map
        } catch (e) {
            serviceLevelMap.value = {}
        }
    }

    // hotelInfo may finish loading after this form mounts — resolve levels once ready.
    watch(
        () => supplierStore.hotelInfo?.hotelId,
        hotelId => {
            if (hotelId && props.serviceType === contractServiceType.ownHotel && !Object.keys(serviceLevelMap.value).length) {
                loadServiceLevels()
            }
        }
    )

    // UI-25: append a sustainability suffix so Basic / Sustainable / Zero variants of the
    // same base room are distinguishable in the "Offer this extra service" list.
    const roomTypeDisplayName = (roomType: any) => {
        const base = roomType?.name ?? ''
        const level = roomType?.sustainabilityLevel || serviceLevelMap.value[roomType?.id]
        if (level === 'SUSTAINABLE') return `${base} (${t('sustainability.sustainable')})`
        if (level === 'ZERO') return `${base} (${t('sustainability.zero')})`
        return base
    }

    const useActiveMealTypes = computed(() => {
        return supplierStore.mealTypes.filter(({active}) => active)
    })

    const membershipLevels = computed(() => supplierStore.membershipLevels)

    const resetPaxes = () => {
        if (formData.value.unitPricingType !== 'PER_PAX' && formData.value.unitPricingType !== 'PER_TOURIST') {
            paxes.value = paxes.value.map(({value, title}) => ({
                value,
                title,
                enabled: true,
            }))
        }
    }

    function initBaseFormData() {
        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                supplierStore.loadCharterServiceCategories()
                return initCharterExtraServices()
            case contractServiceType.ownExcursion:
                supplierStore.loadProductServices()
                return initExcursionExtraServices()
            case contractServiceType.concert:
                supplierStore.loadProductServices()
                return initEventExtraServices()
            case contractServiceType.ownTransfer:
                supplierStore.loadProductServices()
                return initTransferExtraServices()
            case contractServiceType.ownCarRent:
                return initCarHireExtraServices()
            case contractServiceType.ownCruise:
                supplierStore.loadProductServices()
                return initCruiseExtraServices()
            case contractServiceType.tour:
                supplierStore.loadProductServices()
                return initCruiseExtraServices()
            case contractServiceType.ownExtraService:
                supplierStore.loadProductServices()
                return initCruiseExtraServices()
            case contractServiceType.ownHotel:
                supplierStore.loadProductServices()
                // ZSH override: initAccommodationExtraServices now defaults to BY_NIGHT
                return initAccommodationExtraServices()
            case contractServiceType.ownVisa:
                supplierStore.loadProductServices()
                return initVisaExtraServices()
            default:
                return {}
        }
    }

    //TODO[p4] refactoring? replace formData.hasOwnProperty with a serviceType check
    const usePreparedExtraService = computed(() => {
        const {name, active, description, forAllPaxes, categoryId} = props.editableExtraService
        const extraService = props.editableExtraService
        const baseFormData = {name, active, description}

        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                return Object.assign(baseFormData, {
                    extraServiceCategory: extraService.extraServiceCategory,
                    unitPricingType: extraService.unitPricingType,
                    providerType: extraService.providerType,
                    enabledForPaxes: extraService.enabledForPaxes,
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServiceCategories: extraService.enabledForServiceCategories,
                    amount: extraService.amount,
                    limit: extraService.limit,
                    unitOfMeasure: extraService.unitOfMeasure,
                    forAllPaxes,
                })
            case contractServiceType.ownExcursion:
                return Object.assign(baseFormData, {
                    unitPricingType: extraService.unitPricingType,
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    forAllPaxes,
                })
            case contractServiceType.concert:
                return Object.assign(baseFormData, {
                    unitPricingType: extraService.unitPricingType,
                    calculationType: extraService.calculationType,
                    providerType: extraService.providerType,
                    enabledForPaxes: extraService.enabledForPaxes,
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    categoryId,
                    forAllPaxes,
                })
            case contractServiceType.ownTransfer:
                return Object.assign(baseFormData, {
                    unitPricingType: extraService.unitPricingType,
                    providerType: extraService.providerType,
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    categoryId,
                    forAllPaxes,
                })
            case contractServiceType.ownCarRent:
                return Object.assign(baseFormData, {
                    calculationType: extraService.calculationType,
                    extraServiceApplicationType: extraService.extraServiceApplicationType,
                    officeCombinations: extraService.officeCombinations,
                    categoryId,
                })
            case contractServiceType.ownCruise:
                return Object.assign(baseFormData, {
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    enabledForPaxes: extraService.enabledForPaxes,
                    unitPricingType: extraService.unitPricingType,
                    calculationType: extraService.calculationType,
                    providerType: extraService.providerType,
                    categoryId,
                    forAllPaxes,
                })
            case contractServiceType.tour:
                return Object.assign(baseFormData, {
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    enabledForPaxes: extraService.enabledForPaxes,
                    unitPricingType: extraService.unitPricingType,
                    calculationType: extraService.calculationType,
                    providerType: extraService.providerType,
                    categoryId,
                    forAllPaxes,
                })
            case contractServiceType.ownExtraService:
                return Object.assign(baseFormData, {
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    enabledForPaxes: extraService.enabledForPaxes,
                    unitPricingType: extraService.unitPricingType,
                    calculationType: extraService.calculationType,
                    providerType: extraService.providerType,
                    categoryId,
                    forAllPaxes,
                })
            case contractServiceType.ownHotel:
                return extraService
            case contractServiceType.ownVisa:
                return Object.assign(baseFormData, {
                    enabledAllForServices: extraService.enabledAllForServices,
                    enabledForServices: extraService.enabledForServices,
                    unitPricingType: extraService.unitPricingType,
                    forAllPaxes,
                    enabledForPlaceTypes: extraService.enabledForPlaceTypes,
                    forAllPlaceTypes: extraService.forAllPlaceTypes,
                })
            default:
                return baseFormData
        }
    })

    const useIsLoadServiceCategories = computed(() => {
        const availableServices: string[] = [
            contractServiceType.ownExcursion,
            contractServiceType.concert,
            contractServiceType.ownTransfer,
            contractServiceType.ownCruise,
            contractServiceType.tour,
            contractServiceType.ownExtraService,
            contractServiceType.ownVisa,
        ]
        return availableServices.includes(props.serviceType)
    })

    const useHeaders = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                return [
                    {title: t('flight_number'), value: 'name', minWidth: '120px'},
                    {title: t('status'), value: 'active', minWidth: '120px', width: '10%'},
                ]
            case contractServiceType.ownTransfer:
                return [
                    {title: t('transport'), value: 'categoryId', minWidth: '120px'},
                    {title: t('status'), value: 'active', minWidth: '120px', width: '10%'},
                ]
            default:
                return [
                    {title: t('name'), value: 'name', minWidth: '120px'},
                    {title: t('category'), value: 'categoryId'},
                    {title: t('status'), value: 'active', minWidth: '120px', width: '10%'},
                ]
        }
    })

    const useServiceHeaders = computed(() => {
        return [{title: t('supplier.all'), value: 'name'}]
    })

    const useMemberShipLevelHeaders = computed(() => {
        return [{title: t('supplier.all'), value: 'code'}]
    })

    const updateCheckIn = () => {
        if (formData.value.extraServiceCategoryName === 'EARLY_CHECKIN') {
            formData.value.startTime = '00:00'
            formData.value.endTime = '11:00'
        }
        if (formData.value.extraServiceCategoryName === 'LATE_CHECKOUT') {
            formData.value.startTime = '13:00'
            formData.value.endTime = '23:00'
        }
        if (
            formData.value.extraServiceCategoryName === 'EARLY_CHECKIN' ||
            formData.value.extraServiceCategoryName === 'LATE_CHECKOUT'
        ) {
            formData.value.immediateConfirmation = true
        } else {
            formData.value.immediateConfirmation = false
            delete formData.value.startTime
            delete formData.value.endTime
        }

        // Handle eco categories
        if (
            formData.value.extraServiceCategoryName === 'ECO_AMENITIES' ||
            formData.value.extraServiceCategoryName === 'ENERGY_SAVING_SOLUTIONS'
        ) {
            // For eco categories, set to Service / By night
            formData.value.unitPricingType = 'PER_UNIT'
            formData.value.calculationType = 'BY_NIGHT'
        } else if (
            formData.value.extraServiceCategoryName &&
            formData.value.hasOwnProperty('unitPricingType') &&
            formData.value.hasOwnProperty('calculationType')
        ) {
            // For ZSH tenant hotel services, only set BY_NIGHT for NEW services (not when editing existing ones)
            formData.value.unitPricingType = 'PER_UNIT'
            if (!props.editableExtraService) {
                formData.value.calculationType = 'BY_NIGHT'
            }
        }
    }

    const generateTimeArray = (from, to) => {
        let timeArray = []
        for (let i = from; i <= to; i++) {
            let hours = i.toString().padStart(2, '0')
            timeArray.push(`${hours}:00`)
        }
        return timeArray
    }

    const useStartTime = computed(() => {
        let time = []
        if (formData.value.extraServiceCategoryName === 'EARLY_CHECKIN') {
            time = generateTimeArray(0, 11)
        }
        if (formData.value.extraServiceCategoryName === 'LATE_CHECKOUT') {
            time = generateTimeArray(13, 23)
        }
        return time
    })

    const useEndTime = computed(() => {
        let time = []
        if (formData.value.extraServiceCategoryName === 'EARLY_CHECKIN') {
            time = generateTimeArray(0, 11)
        }
        if (formData.value.extraServiceCategoryName === 'LATE_CHECKOUT') {
            time = generateTimeArray(13, 23)
        }
        return time
    })

    const save = async (): Promise<void> => {
        const service = clone(formData.value)
        if (service?.enabledForPaxes) {
            service.enabledForPaxes = paxes.value.filter(({enabled}) => enabled).map(({value}) => value)
            service.forAllPaxes = true
        }

        if (props.serviceType === contractServiceType.ownHotel) {
            service.enabledAllForServices = useActiveRoomTypes.value?.length === service.enabledForServices?.length
            service.forAllSecondaryServices =
                useActiveMealTypes.value?.length === service.enabledForSecondaryServices?.length
            service.forAllMembershipLevels = membershipLevels.value?.length === service.membershipLevels?.length
            // Always set forAllPlaceTypes to true
            service.forAllPlaceTypes = true

            // Populate enabledForPlaceTypes with all allocation types
            service.enabledForPlaceTypes = useAllocationTypes.value.map(({id}) => id)

            // Ensure required fields are included
            service.enabledForServiceCategories = service.enabledForServiceCategories || []
            service.immediateConfirmation =
                service.immediateConfirmation !== undefined ? service.immediateConfirmation : false
        }

        // Remove UI-only fields that shouldn't be sent to the API
        if (service.hasOwnProperty('categoryNameSortable')) {
            delete service.categoryNameSortable
        }

        // Remove energy-related fields that are no longer needed
        if (service.hasOwnProperty('energyType')) {
            delete service.energyType
        }
        if (service.hasOwnProperty('hoursOfUsage')) {
            delete service.hoursOfUsage
        }
        if (service.hasOwnProperty('kwconsumption')) {
            delete service.kwconsumption
        }
        if (service.hasOwnProperty('co2EmissionFactor')) {
            delete service.co2EmissionFactor
        }
        if (service.hasOwnProperty('costFactor')) {
            delete service.costFactor
        }

        clean(service)

        try {
            isLoading.value = true
            if (props.editableExtraService) {
                await app.$api[props.appMethod].put({
                    id: props.editableExtraService.id,
                    contractId: props.contractId,
                    ...service,
                })
            } else {
                await app.$api[props.appMethod].post({
                    contractId: props.contractId,
                    ...service,
                })
            }
            app.$toast.success(t('success_message.saved_successfully'))
            emit('save')
        } catch (e) {
            app.$toast.error(t('error_message.save_error'))
        } finally {
            isLoading.value = false
        }
    }

    const useCategories = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                return useCharterCategories.value
            default:
                return []
        }
    })

    const useCalculations = computed(() => {
        const exceptions = ['Chauffeur', 'Insurance']
        let availableOptions = []

        switch (props.serviceType) {
            case contractServiceType.ownCarRent:
                if (
                    exceptions.includes(
                        props.extraServiceCategories.find(({id}) => id === formData.value.categoryId)?.label
                    )
                ) {
                    // TODO [P3] Need fix this
                    // eslint-disable-next-line vue/no-side-effects-in-computed-properties
                    formData.value.calculationType = 'BY_DAY'
                    availableOptions = ['BY_DAY']
                } else {
                    availableOptions = ['BY_SERVICE', 'BY_DAY']
                }
                break
            case contractServiceType.ownExtraService:
                availableOptions = ['BY_SERVICE', 'BY_NIGHT', 'BY_DAY', 'BY_HOUR']
                break
            case contractServiceType.ownHotel:
                // For ZSH tenant, only show BY_NIGHT option by default
                availableOptions = ['BY_NIGHT']

                // If editing an existing service with BY_SERVICE, include it as disabled
                if (props.editableExtraService && formData.value.calculationType === 'BY_SERVICE') {
                    availableOptions.unshift('BY_SERVICE')
                }
                break
            default:
                availableOptions = ['BY_SERVICE', 'BY_NIGHT', 'BY_DAY']
                break
        }

        // Convert to objects with value and disabled properties
        return availableOptions.map(option => ({
            value: option,
            disabled:
                props.serviceType === contractServiceType.ownHotel &&
                option === 'BY_SERVICE' &&
                props.editableExtraService &&
                formData.value.calculationType === 'BY_SERVICE',
        }))
    })

    const useCharterCategories = computed(() => {
        const categories: CharterExtraServiceCategory[] = [
            CharterExtraServiceCategory.Baggage,
            CharterExtraServiceCategory.Meal,
            CharterExtraServiceCategory.PriorityBoarding,
            CharterExtraServiceCategory.TaxesAndFees,
            CharterExtraServiceCategory.Other,
        ]
        return categories.map(value => ({value, title: t(`charterExtraServiceCategory.${value}`)}))
    })

    const useUnitPricing = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownHotel:
                return [
                    {value: 'PER_UNIT', title: t('product.info.service')},
                    {value: 'PER_PAX', title: t('product.info.guest')},
                    {value: 'PER_BEDROOM', title: t('product.info.bedroom')},
                ]
            case contractServiceType.ownCharter:
                return [
                    {value: 'PER_SERVICE', title: t('product.info.service')},
                    {value: 'PER_TOURIST', title: t('product.info.tourist')},
                ]
            default:
                // PER_UNIT as PER_SERVICE, PER_PAX as PER_TOURIST
                return [
                    {value: 'PER_UNIT', title: t('product.info.service')},
                    {value: 'PER_PAX', title: t('product.info.tourist')},
                ]
        }
    })

    const useProvider = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                return [
                    {value: 'SUPPLIER', title: t('accountType.SUPPLIER')},
                    {value: 'TOUR_OPERATOR', title: t('companyTypes.TOUR_OPERATOR')},
                ]
            case contractServiceType.ownHotel:
                return [
                    {value: 'HOTEL', title: t('hotel_name')},
                    {value: 'TOUR_OPERATOR', title: t('companyTypes.TOUR_OPERATOR')},
                ]
            default:
                // HOTEL as SUPPLIER
                return [
                    {value: 'HOTEL', title: t('accountType.SUPPLIER')},
                    {value: 'TOUR_OPERATOR', title: t('companyTypes.TOUR_OPERATOR')},
                ]
        }
    })

    const useSuggestTitle = computed(() => {
        switch (props.serviceType) {
            case contractServiceType.ownCharter:
                return t('product.info.suggest_for_charters')
            case contractServiceType.ownTransfer:
                return t('product.info.suggest_for_transfer')
            case contractServiceType.ownCruise:
                return t('product.info.suggest_for_cruise')
            default:
                return t('product.info.suggest_for_all')
        }
    })

    const useAllocationTypes = computed(() => {
        const allocationTypes = ['Base', 'Extra', 'WithoutPlace']
        return allocationTypes.map(value => ({id: value, name: t(`placeType.${value}`)}))
    })

    const useUnitOfMeasure = computed(() => {
        const categories: UnitOfMeasure[] = [UnitOfMeasure.Pieces, UnitOfMeasure.Kilograms, UnitOfMeasure.Pounds]
        return categories.map(value => ({value, title: t(`unitOfMeasure.${value}`)}))
    })

    const useIsShowPaxes = computed(() => {
        const availableFor: string[] = [
            contractServiceType.ownHotel,
            contractServiceType.ownCharter,
            contractServiceType.concert,
            contractServiceType.ownCruise,
            contractServiceType.tour,
            contractServiceType.ownExtraService,
        ]
        return availableFor.includes(props.serviceType)
    })

    const {hasAccess} = useAccess()

    // Watch for changes in meal types and membership levels to sync arrays when data loads
    watch(
        [useActiveMealTypes, membershipLevels],
        () => {
            if (props.serviceType === contractServiceType.ownHotel && formData.value) {
                // Sync meal types
                if (formData.value.forAllSecondaryServices && useActiveMealTypes.value?.length) {
                    formData.value.enabledForSecondaryServices = useActiveMealTypes.value.map(({id}) => id)
                }

                // Sync membership levels
                if (formData.value.forAllMembershipLevels && membershipLevels.value?.length) {
                    formData.value.membershipLevels = membershipLevels.value.map(({id}) => id)
                }
            }
        },
        {immediate: true}
    )
</script>
